import { Box, ListItem, Typography } from "@mui/material";
import { DATE_TIME_FORMAT, formatDateTime } from "@shared";

import { PriorityRequestResponse } from "@/api";
import { ClientLink } from "@/common/components/client-link/ClientLink";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { PriorityRequestRowActions } from "@/pages/priority-requests/PriorityRequests.types";
import { PriorityRequestRowMenu } from "@/pages/priority-requests/components/table/components/PriorityRequestRowMenu";
import { ReachabilityCell } from "@/pages/priority-requests/components/table/components/ReachabilityCell";
import { useTranslation } from "@/translations";

const META_SEPARATOR = " · ";

interface Props {
  priorityRequest: PriorityRequestResponse;
  actions: PriorityRequestRowActions;
}

export function PriorityRequestRow({ priorityRequest, actions }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const locale = locales[language];

  const name = `${priorityRequest.firstName} ${priorityRequest.lastName}`;
  const lastContact = priorityRequest.lastContactAttemptUtc
    ? formatDateTime(priorityRequest.lastContactAttemptUtc, DATE_TIME_FORMAT, locale)
    : t("priorityRequests.noContactAttempt");

  const meta = [
    `${t("priorityRequests.widget.appointment")} ${formatDateTime(priorityRequest.appointmentStartUtc, DATE_TIME_FORMAT, locale)}`,
    `${t("priorityRequests.widget.form")} ${priorityRequest.formFillPercent}%`,
    `${t("priorityRequests.widget.contact")} ${lastContact}`,
  ].join(META_SEPARATOR);

  return (
    <ListItem
      sx={(theme) => ({
        borderBottom: "1px solid",
        borderColor: theme.palette.fsw["blue-10"],
        minHeight: "69px",
        py: 1.5,
        gap: 1,
        alignItems: "flex-start",
      })}
    >
      <Box flex={1} minWidth={0}>
        <ClientLink clientId={priorityRequest.clientId} name={name} variant="body1" />
        <Typography variant="body2" color="text.secondary">
          {meta}
        </Typography>
      </Box>
      {priorityRequest.reachabilityWindows.length > 0 && (
        <Box flexShrink={0}>
          <ReachabilityCell windows={priorityRequest.reachabilityWindows} align="right" />
        </Box>
      )}
      <Box flexShrink={0} alignSelf="center">
        <PriorityRequestRowMenu
          name={name}
          onPullForward={() => actions.onPullForward(priorityRequest)}
          onContactAttempt={() => actions.onContactAttempt(priorityRequest)}
          onDelete={() => actions.onDelete(priorityRequest)}
          isBusy={actions.isBusy}
        />
      </Box>
    </ListItem>
  );
}
